import { createAction, props } from '@ngrx/store';

import BetslipActions from '../../base/store/actions';
import { BetslipType } from '../../core/betslip-type';
import { PickId } from '../../core/picks/pick-id';
import { SlipId } from '../bet-generation/models';
import { BetslipTrackingData } from '../tracking/tracking.models';
import { BetPlacementError } from './errors/bet-placement-error';
import { BetslipError } from './errors/betslip-error';
import { ResultError } from './errors/result/result-error';
import { SlipStakeHint, StakeHintInfo } from './models/stake-validation.models';
import { BetslipTypeErrors, BetslipTypePickErrors, SlipErrors } from './state';

export default class BetslipErrorsActions {
    static readonly ACTION_SCHEMA = `${BetslipActions.ACTION_SCHEMA}/errors`;

    private static readonly PICK_ERRORS_CHECK_FINISHED = `${BetslipErrorsActions.ACTION_SCHEMA}/PICK_ERRORS_CHECK_FINISHED`;
    private static readonly BETSLIP_ERRORS_CHECK_FINISHED = `${BetslipErrorsActions.ACTION_SCHEMA}/BETSLIP_ERRORS_CHECK_FINISHED`;
    private static readonly REMOVE_DOUBLE_BET_PREVENTION_ERROR = `${BetslipErrorsActions.ACTION_SCHEMA}/REMOVE_DOUBLE_BET_PREVENTION_ERROR`;
    private static readonly REMOVE_OFFER_CHANGED_ERROR = `${BetslipErrorsActions.ACTION_SCHEMA}/REMOVE_OFFER_CHANGED_ERROR`;
    private static readonly REMOVE_OVERALL_MAX_WIN_PER_USER_EXCEEDED_ERROR = `${BetslipErrorsActions.ACTION_SCHEMA}/REMOVE_OVERALL_MAX_WIN_PER_USER_EXCEEDED_ERROR`;
    private static readonly CLEAN_MANUAL_REMOVE_ERRORS = `${BetslipErrorsActions.ACTION_SCHEMA}/CLEAN_MANUAL_REMOVE_ERRORS`;
    private static readonly PLACE_BET_RESPONSE_ERRORS = `${BetslipErrorsActions.ACTION_SCHEMA}/PLACE_BET_RESPONSE_ERRORS`;
    private static readonly MARK_ALL_NOTIFY_USER_ERRORS_AS_SEEN = `${BetslipErrorsActions.ACTION_SCHEMA}/MARK_ALL_NOTIFY_USER_ERRORS_AS_SEEN`;
    private static readonly ADD_PICK_ERROR = `${BetslipErrorsActions.ACTION_SCHEMA}/ADD_PICK_ERROR`;
    private static readonly ADD_BETSLIP_ERROR = `${BetslipErrorsActions.ACTION_SCHEMA}/ADD_BETSLIP_ERROR`;
    private static readonly VALIDATE_BETSLIP = `${BetslipErrorsActions.ACTION_SCHEMA}/VALIDATE_BETSLIP`;
    private static readonly ACCEPT_BETSLIP_CHANGES = `${BetslipErrorsActions.ACTION_SCHEMA}/ACCEPT_BETSLIP_CHANGES`;
    private static readonly ACCEPT_BETSLIP_EDIT_BET_CHANGES = `${BetslipErrorsActions.ACTION_SCHEMA}/ACCEPT_BETSLIP_EDIT_BET_CHANGES`;
    private static readonly SHOW_BETSLIP_FULL_DIALOG = `${BetslipErrorsActions.ACTION_SCHEMA}/SHOW_BETSLIP_FULL_DIALOG`;
    private static readonly TRACK_BETSLIP_CLIENT_ERRORS = `${BetslipErrorsActions.ACTION_SCHEMA}/TRACK_BETSLIP_CLIENT_ERRORS`;
    private static readonly TRACK_QUICK_BET_CLIENT_ERRORS = `${BetslipErrorsActions.ACTION_SCHEMA}/TRACK_QUICK_BET_CLIENT_ERRORS`;

    private static readonly LINEAR_ACCEPT_BETSLIP_CHANGES = `${BetslipErrorsActions.ACTION_SCHEMA}/LINEAR_ACCEPT_BETSLIP_CHANGES`;
    private static readonly SET_STAKE_FROM_STAKE_HINTS = `${BetslipErrorsActions.ACTION_SCHEMA}/SET_STAKE_FROM_STAKE_HINTS`;
    private static readonly SET_STAKE_FROM_SLIP_STAKE_HINT = `${BetslipErrorsActions.ACTION_SCHEMA}/SET_STAKE_FROM_SLIP_STAKE_HINT`;

    static picksErrorsCheckFinished = createAction(BetslipErrorsActions.PICK_ERRORS_CHECK_FINISHED, props<{ errors: BetslipTypePickErrors }>());

    static betslipErrorsCheckFinished = createAction(
        BetslipErrorsActions.BETSLIP_ERRORS_CHECK_FINISHED,
        props<{ errors: BetslipError[]; typeSlipErrors: BetslipTypeErrors; slipErrors: SlipErrors }>(),
    );

    static validateBetslip = createAction(BetslipErrorsActions.VALIDATE_BETSLIP);

    static removeDoubleBetPreventionError = createAction(BetslipErrorsActions.REMOVE_DOUBLE_BET_PREVENTION_ERROR);

    static removeOfferChangedError = createAction(BetslipErrorsActions.REMOVE_OFFER_CHANGED_ERROR);
    static removeOverallMaxWinPerUserExceededError = createAction(BetslipErrorsActions.REMOVE_OVERALL_MAX_WIN_PER_USER_EXCEEDED_ERROR);

    static cleanManualRemoveErrors = createAction(BetslipErrorsActions.CLEAN_MANUAL_REMOVE_ERRORS);

    static placeBetResponseErrors = createAction(
        BetslipErrorsActions.PLACE_BET_RESPONSE_ERRORS,
        props<{
            pickErrors: BetslipTypePickErrors;
            betslipErrors: BetslipError[];
            slipErrors: Record<SlipId, BetPlacementError[]>;
        }>(),
    );

    static markAllNotifyUserErrorsAsSeen = createAction(BetslipErrorsActions.MARK_ALL_NOTIFY_USER_ERRORS_AS_SEEN);

    static addPickError = createAction(
        BetslipErrorsActions.ADD_PICK_ERROR,
        props<{ pickId: PickId; error: ResultError; betslipType: BetslipType }>(),
    );

    static addBetslipError = createAction(BetslipErrorsActions.ADD_BETSLIP_ERROR, props<{ error: BetslipError }>());
    static acceptBetslipChanges = createAction(BetslipErrorsActions.ACCEPT_BETSLIP_CHANGES);
    static acceptBetslipEditBetChanges = createAction(BetslipErrorsActions.ACCEPT_BETSLIP_EDIT_BET_CHANGES);

    /**
     * Show betslip full dialog
     */
    static showBetslipFullDialog = createAction(BetslipErrorsActions.SHOW_BETSLIP_FULL_DIALOG);

    /**
     * Track betslip errors
     */
    static trackBetslipClientErrors = createAction(BetslipErrorsActions.TRACK_BETSLIP_CLIENT_ERRORS, props<{ trackingData: BetslipTrackingData }>());

    static linearAcceptBetslipChanges = createAction(BetslipErrorsActions.LINEAR_ACCEPT_BETSLIP_CHANGES);

    static setStakeFromStakeHints = createAction(BetslipErrorsActions.SET_STAKE_FROM_STAKE_HINTS, props<{ stakeHints: StakeHintInfo }>());
    static setStakeFromSlipStakeHint = createAction(BetslipErrorsActions.SET_STAKE_FROM_SLIP_STAKE_HINT, props<{ slipStakeHint: SlipStakeHint }>());
    static trackQuickBetClientErrors = createAction(
        BetslipErrorsActions.TRACK_QUICK_BET_CLIENT_ERRORS,
        props<{ trackingData: BetslipTrackingData }>(),
    );
}
